import { useEffect, useRef, useState, type FormEvent, type JSX } from 'react';

import { cn } from '../lib/cn.js';
import { IconArrowRight } from '../icons/index.js';
import { ChampAvecMentions } from './ChampAvecMentions.js';
import type { EntreeDeMention } from './mentions.js';
import type { PortDeFil } from './port.js';
import { brouillonApresEchec, creerFileDEnvoi, messageDEchecDEnvoi, type FileDEnvoi } from './fileDEnvoi.js';

export interface ComposeurConversationProps {
  conversationId: string;
  port: Pick<PortDeFil, 'envoyer'>;
  entrees: readonly EntreeDeMention[];
  /** Repondre dans un fil : la racine. */
  parentId?: string | undefined;
  placeholder?: string | undefined;
  /** Le panneau de 24rem du centre : marges serrees, pas de legende. */
  compact?: boolean | undefined;
  autoFocus?: boolean | undefined;
  disabled?: boolean | undefined;
  /** Pourquoi on ne peut pas ecrire (G16) : le champ le dit a la place du placeholder. */
  lectureSeule?: string | undefined;
  /**
   * Appele AVANT l'envoi : rend la conversation ou ecrire (le chatter d'une
   * fiche est cree au premier message par `ouvrirChatter`). Absent :
   * `conversationId` tel quel. Appele UNE fois tant qu'il reussit : trois
   * messages tapes vite avant que le chatter existe n'en creent qu'un. L'hote
   * garde le composeur MONTE pendant cette creation (sa `key` ne suit pas
   * l'id qui nait), sinon la file d'envoi partirait avec lui.
   */
  avantEnvoi?: (() => Promise<string>) | undefined;
  onEnvoye?: ((texte: string) => void) | undefined;
  /**
   * Un texte a poser en tete du brouillon (« Demander a Lia » du centre pose
   * `@Lia `) ; `n` change a chaque demande, meme pour un texte identique.
   */
  insertion?: { texte: string; n: number } | undefined;
}

/**
 * LE COMPOSEUR D'UNE CONVERSATION : une bande filetee en bas (pas une boite),
 * le champ a mentions, Entree envoie, Maj+Entree saute une ligne, l'erreur
 * en ligne avec sa raison (G16). Pilote par props : `port={sdk.messages}`.
 *
 * ENVOI SANS BLOCAGE (retouches du 06/10/2026) : Entree vide le champ et garde
 * le focus tout de suite ; le message part en arriere-plan, dans l'ordre
 * (`fileDEnvoi.ts`). Un echec remet le texte dans le brouillon, avant ce qu'on
 * a tape depuis, et dit pourquoi.
 */
export function ComposeurConversation({
  conversationId, port, entrees, parentId, placeholder, compact, autoFocus, disabled, lectureSeule, avantEnvoi, onEnvoye, insertion,
}: ComposeurConversationProps): JSX.Element {
  const [brouillon, setBrouillon] = useState('');
  useEffect(() => {
    if (!insertion || insertion.texte === '') return;
    setBrouillon((courant) => courant.startsWith(insertion.texte) ? courant : `${insertion.texte}${courant}`);
  }, [insertion?.n]);
  const [erreur, setErreur] = useState<string | null>(null);
  const inerte = disabled === true || lectureSeule !== undefined;
  const formulaire = useRef<HTMLFormElement>(null);
  // Le brouillon a l'instant : deux Entree avant le rendu suivant lisent le
  // meme `brouillon` d'etat ; la ref, videe au premier, empeche le double envoi.
  const brouillonCourant = useRef(brouillon);
  brouillonCourant.current = brouillon;
  const onEnvoyeCourant = useRef(onEnvoye);
  onEnvoyeCourant.current = onEnvoye;
  // La conversation creee par `avantEnvoi`, partagee par les envois en file.
  const cibleCreee = useRef<Promise<string> | null>(null);
  const file = useRef<FileDEnvoi<null> | null>(null);
  file.current ??= creerFileDEnvoi<null>({
    surEnvoye: ({ texte }) => onEnvoyeCourant.current?.(texte),
    surEchec: (rendus, cause) => {
      setBrouillon((courant) => brouillonApresEchec(rendus.map((r) => r.texte), courant));
      setErreur(messageDEchecDEnvoi(cause, rendus.length));
    },
  });

  function envoyer(): void {
    const texte = brouillonCourant.current.trim();
    if (texte === '' || inerte) return;
    brouillonCourant.current = '';
    setBrouillon('');
    setErreur(null);
    formulaire.current?.querySelector('textarea')?.focus({ preventScroll: true });
    const options = parentId !== undefined ? { parentId } : undefined;
    const creer = avantEnvoi;
    file.current?.ajouter(texte, null, async () => {
      let cible = conversationId;
      if (creer !== undefined) {
        cibleCreee.current ??= creer();
        try { cible = await cibleCreee.current; } catch (cause) { cibleCreee.current = null; throw cause; }
      }
      await port.envoyer(cible, texte, options);
    });
  }

  function onSubmit(event: FormEvent): void {
    event.preventDefault();
    envoyer();
  }

  return <form ref={formulaire} onSubmit={onSubmit} data-composeur-conversation
    className={cn('shrink-0 bg-surface', compact ? 'px-2.5 pb-2.5 pt-1' : 'px-5 pb-3 pt-1.5')}>
    {/* P21 : le champ sur un fond doux, sans contour, meme au clic ; aucun filet au-dessus. */}
    <div data-composeur-champ className="taki-champ-de-saisie flex items-end gap-2">
      <ChampAvecMentions
        value={brouillon}
        onChange={setBrouillon}
        entrees={entrees}
        onEnvoyer={envoyer}
        placeholder={lectureSeule ?? placeholder ?? 'Écrire un message… @ cite une personne'}
        ariaLabel="Votre message"
        autoFocus={autoFocus}
        disabled={inerte}
      />
      <button type="submit" disabled={!brouillon.trim() || inerte} aria-label="Envoyer le message" title="Envoyer le message"
        className="relative mb-[1px] flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-accent text-accent-label disabled:bg-surface-3 disabled:text-ink-400 focus-visible:shadow-ring after:absolute after:-inset-1.5 after:content-['']">
        <span aria-hidden="true" className="-rotate-90"><IconArrowRight size={14} /></span>
      </button>
    </div>
    {erreur !== null && <p role="alert" className="mt-1.5 text-xs leading-4 text-danger-ink">{erreur}</p>}
    {!compact && erreur === null && <p className="mt-1.5 text-[0.6875rem] leading-4 text-ink-400">
      Entrée envoie · Maj+Entrée saute une ligne · @ cite une personne · @Lia répond une fois, avec vos droits
    </p>}
  </form>;
}
